import { EntityRepository } from '@/interfaces'
import model from './model'
import type ObjectInterface from './interface'
import { hydrateEntityObject } from '@/entities/lib'
import graphQLClient from '@/lib/api/graphQLClient'
import { TFunction } from 'next-i18next'
import { useMemo } from 'react'
import useSWR from 'swr'
import { uppercaseFirst } from '@/lib/helpers/helpers'
import bargainTierObject from '@/entities/bargainTier/interface'

import {
  ALL_BARGAIN_CONFIGS_QUERY,
  SINGLE_BARGAIN_CONFIG_QUERY,
  BARGAIN_CONFIGS_DEPENDENTS_QUERY,
  CREATE_BARGAIN_CONFIG_MUTATION,
  UPDATE_BARGAIN_CONFIG_MUTATION,
  DELETE_BARGAIN_CONFIG_MUTATION,
  FORCE_DELETE_BARGAIN_CONFIG_MUTATION,
  ADD_BARGAIN_CONFIG_TIERS_MUTATION,
  REMOVE_BARGAIN_CONFIG_TIERS_MUTATION,
  CREATE_BARGAIN_CONFIG_TIERS_MUTATION,
  DELETE_BARGAIN_CONFIG_TIERS_MUTATION,
  UPDATE_BARGAIN_CONFIG_TIERS_ORDER_MUTATION,
} from './graphql'

const hydrateObjects = (objects: Array<any>) => {
  return objects.map((object) => hydrateEntityObject(model, object))
}

const hydrateObject = (object: any) => {
  return hydrateEntityObject(model, object)
}
const ratioBargainConfigRepository: EntityRepository = {
  getObject: async (id: string | number, includes = {}): Promise<any> => {
    const variables = { id: id, ...includes }
    return graphQLClient.request(SINGLE_BARGAIN_CONFIG_QUERY, variables).then((data: any) => {
      return hydrateObject(data.bargainConfig)
    })
  },

  getObjects: async (includes = {}): Promise<any> => {
    return graphQLClient.request(ALL_BARGAIN_CONFIGS_QUERY, includes).then((data: any) => {
      return hydrateObjects(data.bargainConfigs)
    })
  },

  useObject: (id: string | number, includes = {}, paused = false) => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const variables = useMemo(() => ({ id: id, ...includes }), [id])
    const { data, error, mutate } = useSWR(paused ? null : [SINGLE_BARGAIN_CONFIG_QUERY, variables], { revalidateOnFocus: false })
    return {
      object: !data ? data : hydrateObject(data.bargainConfig),
      isLoading: !paused && !error && !data,
      isError: error,
      mutate: mutate,
    }
  },

  useObjects: (includes = {}, paused = false) => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const variables = useMemo(() => includes, [])
    const { data, error, mutate } = useSWR(paused ? null : [ALL_BARGAIN_CONFIGS_QUERY, variables])
    return {
      objects: !data ? data : hydrateObjects(data.bargainConfigs),
      isLoading: !paused && !error && !data,
      isError: error,
      mutate: mutate,
    }
  },

  getDependents: (id: string | number, includes: []) => {
    const includeVars: { [x: string]: boolean } = {}
    includes.forEach((include) => {
      includeVars[`with${uppercaseFirst(include)}`] = true
    })
    const variables = { id: id, ...includeVars }
    return graphQLClient.request(BARGAIN_CONFIGS_DEPENDENTS_QUERY, variables).then((data: any) => {
      return data.groupedDependents
    })
  },

  useDependents: (id: string | number, includes: []) => {
    const includeVars: { [x: string]: boolean } = {}
    includes.forEach((include) => {
      includeVars[`with${uppercaseFirst(include)}`] = true
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const variables = useMemo(() => ({ id: id, ...includeVars }), [id])
    const { data, error, mutate } = useSWR([BARGAIN_CONFIGS_DEPENDENTS_QUERY, variables], {
      revalidateOnFocus: false,
    })
    return {
      data: !data ? data : data.groupedDependents,
      isLoading: !error && !data,
      isError: error,
      mutate: mutate,
    }
  },

  createObject: async (data: ObjectInterface, includes = {}): Promise<any> => {
    const variables = {
      input: data,
      ...includes,
    }
    return graphQLClient.request(CREATE_BARGAIN_CONFIG_MUTATION, variables).then((data: any) => {
      return hydrateObject(data.bargainConfig)
    })
  },

  updateObject: async (data: ObjectInterface, includes = {}): Promise<any> => {
    const variables = {
      input: data,
      ...includes,
    }
    return graphQLClient.request(UPDATE_BARGAIN_CONFIG_MUTATION, variables).then((data: any) => {
      return hydrateObject(data.bargainConfig)
    })
  },

  deleteObject: async (id: string | number): Promise<any> => {
    const variables = {
      input: { id: id },
    }
    return graphQLClient.request(DELETE_BARGAIN_CONFIG_MUTATION, variables).then((data: any) => {
      return data.deleteRatioBargainConfig
    })
  },

  forceDeleteObject: async (id: string | number): Promise<any> => {
    const variables = {
      input: { id: id },
    }
    return graphQLClient.request(FORCE_DELETE_BARGAIN_CONFIG_MUTATION, variables).then((data: any) => {
      return data.forceDeleteRatioBargainConfig
    })
  },

  assignTiers: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        tierIds: itemIds,
      },
    }
    return graphQLClient.request(ADD_BARGAIN_CONFIG_TIERS_MUTATION, variables)
  },

  unassignTiers: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        tierIds: itemIds,
      },
    }
    return graphQLClient.request(REMOVE_BARGAIN_CONFIG_TIERS_MUTATION, variables)
  },

  createTiers: async (id: string, items: Array<bargainTierObject>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        tiers: items,
      },
    }
    return graphQLClient.request(CREATE_BARGAIN_CONFIG_TIERS_MUTATION, variables)
  },

  deleteTiers: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        tierIds: itemIds,
      },
    }
    return graphQLClient.request(DELETE_BARGAIN_CONFIG_TIERS_MUTATION, variables)
  },

  updateTiersOrder: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        tierIds: itemIds,
      },
    }
    return graphQLClient.request(UPDATE_BARGAIN_CONFIG_TIERS_ORDER_MUTATION, variables)
  },
}

export default ratioBargainConfigRepository
